/**
 * webexCloudOverride
 * -------------------
 * Lets a **GCC-High** Microsoft Teams package serve **commercial** Webex
 * account users by carrying an extra parameter in the Teams manifest tab URL:
 *
 *     contentUrl: "https://msteams-calling-high.webexgov.us/?webexCloud=commercial"
 *
 * When that parameter is present:
 *  - Microsoft stays on GCC-High (`login.microsoftonline.us` / `graph.microsoft.us`)
 *    using the **customer-provided** GCC-High Azure app (registered in Azure
 *    Government), not the standard Cisco gov app.
 *  - **All** integration API calls (Microsoft OBO + Webex token exchange) go to
 *    the **commercial** `jabber-integration` deployment — no gov microservice
 *    boundary crossing.
 *  - Webex OAuth / API / U2C / Hydra use the commercial cloud.
 *
 * Source of truth = the URL only.
 *  - The override is derived **solely** from the current window's query string.
 *    There is intentionally NO localStorage / sticky fallback, so a package
 *    whose contentUrl does NOT carry the parameter ALWAYS resolves to its
 *    host-based config (Prod / Fedramp / Fedramp_High) with no chance of a
 *    leftover flag from another variant on the same origin leaking in.
 *  - Because the OAuth loader routes (`/webexteams/*-auth/loader`) are separate
 *    window navigations that don't inherit the tab's query string, the caller
 *    threads the parameter into the auth-start popup URL via
 *    `webexCloudQuerySuffix()` (see MsTeamsAuthenticationService.startWebexAuth).
 *
 * Security / safety notes:
 *  - The parameter only selects between *public* backend endpoints and *public*
 *    OAuth client identifiers. It carries no secrets.
 *  - The override is allow-listed: it is only honoured when the base
 *    configuration is a FedRAMP / GCC-High environment. A commercial host can
 *    never be flipped into a different cloud by a stray query string.
 *  - The Webex OAuth `redirect_uri` is deliberately left on the GCC-High host
 *    (the app is served from there); that exact URI must be registered as an
 *    allowed redirect on the commercial Webex OAuth client.
 */

import { APP_ENVIRONMENT, isFedRampEnvironment } from './environmentTypes';

/** Manifest query-string parameter name (kept in sync with the manifest contentUrl). */
export const WEBEX_CLOUD_PARAM = 'webexCloud';

/** Supported Webex cloud overrides. */
export enum WebexCloud {
  COMMERCIAL = 'commercial',
}

/** Commercial `jabber-integration` base URL (MS OBO + Webex `/webex/*`). */
export const COMMERCIAL_JABBER_INTEGRATION_URL =
  'https://jabber-integration-a.wbx2.com/jabber-integration/api/v1';

/**
 * Customer GCC-High Azure app ID (Azure Government portal). Must match the
 * `cisco_call_for_government_gcc_high_commercial` manifest `id` /
 * `webApplicationInfo.id` and the commercial `jabber-integration` config.
 */
export const GCC_HIGH_COMMERCIAL_CUSTOMER_CLIENT_ID =
  '1cf89411-4ad9-41ea-bc28-dc561ba8f79f';

/**
 * Fields swapped when the commercial override is active.
 *
 * Microsoft cloud endpoints (`SCOPE`, `GRAPH_INTEGRATION_URL`, `MS_LOGIN_BASE_URL`,
 * `REDIRECT_URI`, `WEBEX_REDIRECT_URI`) stay on GCC-High values from the base
 * config. `CLIENT_ID` / `INTEGRATION_APP_ID` switch to the customer Azure app.
 * `JABBER_INTEGRATION_URL` switches to commercial `jabber-integration` so the
 * flow never calls gov `wxc-msteams-integration`.
 */
export interface WebexBackendConfig {
  WEBEX_CLIENT_ID: string;
  WEBEX_BASE_URL: string;
  WEBEX_SCOPE: string;
  WEBEX_U2C_BASE_URL: string;
  HYDRA_SERVICE_URL: string;
  /**
   * Webex-side integration base URL (`/webex/*`, incl. `/webex/token`).
   * For the commercial override this is the COMMERCIAL `jabber-integration`
   * service, which already brokers the commercial Webex token exchange
   * (client `Cee6d7f0…`) and runs calling-backend detection / cross-launch
   * against commercial CI. The Microsoft OBO stays on the gov
   * `JABBER_INTEGRATION_URL` (not overridden).
   *
   * Optional in the shape so the base app config (which doesn't define it)
   * still satisfies it; the commercial override always sets it.
   */
  WEBEX_INTEGRATION_URL?: string;
  WEBEX_ATTENDANT_CONSOLE: string;
}

export const COMMERCIAL_WEBEX_BACKEND: WebexBackendConfig = {
  WEBEX_CLIENT_ID: 'Cee6d7f0250fb07f4a789316363333d5b74d2ba2cb09a1d00e659d3368b47b5f4',
  WEBEX_BASE_URL: 'https://webexapis.com',
  WEBEX_SCOPE:
    'spark:xsi spark:organizations_read spark:calls_read spark:calls_write broadworks-connector:user Identity:SCIM Identity:Config spark:all bc-sp-standard:user bc-sp-basic:user webexsquare:get_conversation',
  WEBEX_U2C_BASE_URL: 'https://u2c.wbx2.com/u2c/api/v1',
  HYDRA_SERVICE_URL: 'https://api.ciscospark.com/v1',
  WEBEX_INTEGRATION_URL: COMMERCIAL_JABBER_INTEGRATION_URL,
  WEBEX_ATTENDANT_CONSOLE: 'Y2lzY29zcGFyazovL3VzL0FQUExJQ0FUSU9OLzY2NGUzZmJhLWQxYTgtNGYyNi1hOTMyLTMxMTJlOTBiNzJmZA',
};

/** Microsoft + integration overrides for the commercial-Webex GCC-High variant. */
export interface CommercialGccHighMsConfig {
  JABBER_INTEGRATION_URL: string;
  CLIENT_ID: string;
  INTEGRATION_APP_ID: string;
}

export const COMMERCIAL_GCC_HIGH_MS_CONFIG: CommercialGccHighMsConfig = {
  JABBER_INTEGRATION_URL: COMMERCIAL_JABBER_INTEGRATION_URL,
  CLIENT_ID: GCC_HIGH_COMMERCIAL_CUSTOMER_CLIENT_ID,
  INTEGRATION_APP_ID: GCC_HIGH_COMMERCIAL_CUSTOMER_CLIENT_ID,
};

/** Minimal shape the override needs from the app config. */
export interface WebexCloudConfigShape extends WebexBackendConfig {
  APP_ENVIRONMENT: string;
  CLIENT_ID?: string;
  INTEGRATION_APP_ID?: string;
  JABBER_INTEGRATION_URL?: string;
}

/**
 * Resolve the effective Webex-cloud override from a query string.
 *
 * The current window's `window.location.search` is the ONLY input — there is no
 * localStorage / sticky fallback. Unknown or absent values resolve to
 * `undefined` (i.e. "use the host-based config"). Pure.
 */
export function resolveWebexCloudOverride(search?: string | null): WebexCloud | undefined {
  return normalizeWebexCloud(readParamFromSearch(search));
}

/**
 * Build the query-string suffix used to thread the active override into a
 * child navigation (e.g. the Webex auth-start popup URL), so that window
 * resolves the same cloud from its own URL rather than relying on shared state.
 *
 * Returns `&webexCloud=commercial` when the supplied environment is the
 * commercial-Webex GCC-High override, otherwise an empty string.
 */
export function webexCloudQuerySuffix(appEnvironment: string | undefined): string {
  return appEnvironment === APP_ENVIRONMENT.GCC_HIGH_COMMERCIAL_WEBEX_ENVIRONMENT
    ? `&${WEBEX_CLOUD_PARAM}=${WebexCloud.COMMERCIAL}`
    : '';
}

/**
 * Apply the commercial-Webex override to a base config.
 *
 * Returns a NEW object (does not mutate). The override is a no-op unless:
 *  - `override` is `WebexCloud.COMMERCIAL`, and
 *  - the base config is a FedRAMP / GCC-High environment (allow-list guard).
 *
 * When applied it swaps the Webex backend fields to commercial values and sets
 * `APP_ENVIRONMENT` to `GCC_HIGH_COMMERCIAL_WEBEX_ENVIRONMENT` so the Webex SDK
 * initialises in commercial mode (see WebexInstanceService / isFedRampEnvironment).
 */
export function applyWebexCloudOverride<T extends WebexCloudConfigShape>(
  baseConfig: T,
  override: WebexCloud | undefined,
): T {
  if (override !== WebexCloud.COMMERCIAL) {
    return baseConfig;
  }
  if (!isFedRampEnvironment(baseConfig.APP_ENVIRONMENT)) {
    // Allow-list: never reroute a non-gov host based on a query string.
    return baseConfig;
  }
  return {
    ...baseConfig,
    ...COMMERCIAL_WEBEX_BACKEND,
    ...COMMERCIAL_GCC_HIGH_MS_CONFIG,
    APP_ENVIRONMENT: APP_ENVIRONMENT.GCC_HIGH_COMMERCIAL_WEBEX_ENVIRONMENT,
  };
}

function readParamFromSearch(search?: string | null): string | undefined {
  if (!search) {
    return undefined;
  }
  try {
    const value = new URLSearchParams(search).get(WEBEX_CLOUD_PARAM);
    return value ?? undefined;
  } catch {
    return undefined;
  }
}

function normalizeWebexCloud(raw?: string | null): WebexCloud | undefined {
  if (!raw) {
    return undefined;
  }
  const normalized = raw.trim().toLowerCase();
  return normalized === WebexCloud.COMMERCIAL ? WebexCloud.COMMERCIAL : undefined;
}
